import { interviewService } from '../services/interviewService';
import {
  interviewPerformanceNow,
  trackInterviewDuration,
  trackInterviewMetric,
} from './interviewObservability';

export type InterviewVideoUploadStatus = 'queued' | 'uploading' | 'retry_waiting' | 'uploaded' | 'failed';

export type InterviewVideoUploadInput = {
  interviewId: number;
  questionId: number;
  clientAttemptId: string;
  traceId: string;
  blob: Blob;
  recordedAt: string;
  durationSeconds: number;
};

export type InterviewVideoUploadResult = {
  url: string;
  mimeType: string;
  sizeBytes: number;
  durationSeconds: number;
  clientAttemptId: string;
};

export type InterviewVideoUploadSnapshot = {
  key: string;
  interviewId: number;
  questionId: number;
  clientAttemptId: string;
  traceId: string;
  status: InterviewVideoUploadStatus;
  attempts: number;
  retryable: boolean;
  hasBlob: boolean;
  errorCode: string | null;
  result: InterviewVideoUploadResult | null;
};

export type InterviewVideoUploadRunner = (
  input: InterviewVideoUploadInput
) => Promise<InterviewVideoUploadResult>;

type InternalVideoUploadTask = Omit<InterviewVideoUploadInput, 'blob'> & {
  blob: Blob | null;
  key: string;
  status: InterviewVideoUploadStatus;
  attempts: number;
  retryable: boolean;
  errorCode: string | null;
  result: InterviewVideoUploadResult | null;
  sequence: number;
};

type QueueOptions = {
  concurrency?: number;
  uploader?: InterviewVideoUploadRunner;
  maxRetainedUploaded?: number;
  maxAttempts?: number;
  retryDelaysMs?: number[];
  retryScheduler?: (callback: () => void, delayMs: number) => unknown;
};

export const INTERVIEW_VIDEO_UPLOAD_MAX_ATTEMPTS = 3;
export const INTERVIEW_VIDEO_UPLOAD_RETRY_DELAYS_MS = [2_000, 5_000] as const;

const getUploadErrorCode = (error: any) => String(
  error?.response?.data?.error_code
  || error?.response?.data?.error
  || error?.code
  || error?.message
  || 'VIDEO_UPLOAD_FAILED'
).trim().slice(0, 120);

const isRetryableUploadError = (error: any) => {
  const errorCode = getUploadErrorCode(error).toUpperCase();
  if (errorCode === 'STALE_INTERVIEW_QUESTION') return false;

  const explicitRetryable = error?.response?.data?.retryable;
  if (typeof explicitRetryable === 'boolean') return explicitRetryable;

  const status = Number(error?.response?.status);
  if (Number.isFinite(status) && status >= 400 && status < 500) return false;
  return true;
};

export const buildInterviewVideoUploadKey = ({
  interviewId,
  questionId,
  clientAttemptId,
}: Pick<InterviewVideoUploadInput, 'interviewId' | 'questionId' | 'clientAttemptId'>) => (
  `${Number(interviewId)}:${Number(questionId)}:${String(clientAttemptId).trim()}`
);

export const submitAnswerWithoutWaitingForVideo = async <T,>({
  startVideoUpload,
  submitAnswer,
}: {
  startVideoUpload?: (() => void) | null;
  submitAnswer: () => Promise<T>;
}) => {
  try {
    startVideoUpload?.();
  } catch (_) {
    // Supporting video must never prevent the canonical answer attempt.
  }
  return submitAnswer();
};

export const uploadInterviewVideoTask: InterviewVideoUploadRunner = async (input) => {
  const uploadStartedAt = interviewPerformanceNow();
  const observabilityContext = {
    traceId: input.traceId,
    interviewId: input.interviewId,
    questionId: input.questionId,
    clientAttemptId: input.clientAttemptId,
  };

  try {
    const uploadResponse = await interviewService.uploadAnswerVideo(input.blob, {
      clientAttemptId: input.clientAttemptId,
      recordedAt: input.recordedAt,
      videoDurationSeconds: input.durationSeconds,
      interviewId: input.interviewId,
      questionId: input.questionId,
      path: `interview/${input.interviewId}`,
      filename: `q-${input.questionId}.webm`,
      observability: observabilityContext,
    });
    const uploadedVideoUrl = String(uploadResponse?.data?.data?.url || '').trim();
    if (!uploadedVideoUrl) {
      throw new Error('UPLOAD_RESPONSE_MISSING_URL');
    }

    const result = {
      url: uploadedVideoUrl,
      mimeType: input.blob.type.split(';')[0] || 'video/webm',
      sizeBytes: input.blob.size,
      durationSeconds: input.durationSeconds,
      clientAttemptId: input.clientAttemptId,
    };
    trackInterviewDuration('video_upload_ms', uploadStartedAt, {
      ...observabilityContext,
      outcome: 'success',
      httpStatus: Number(uploadResponse.status) || null,
    });
    return result;
  } catch (error: any) {
    trackInterviewDuration('video_upload_ms', uploadStartedAt, {
      ...observabilityContext,
      outcome: 'error',
      httpStatus: Number(error?.response?.status) || null,
    });
    throw error;
  }
};

export class InterviewVideoUploadQueue {
  private readonly concurrency: number;

  private readonly uploader: InterviewVideoUploadRunner;

  private readonly maxRetainedUploaded: number;

  private readonly maxAttempts: number;

  private readonly retryDelaysMs: number[];

  private readonly retryScheduler: (callback: () => void, delayMs: number) => unknown;

  private readonly tasks = new Map<string, InternalVideoUploadTask>();

  private readonly waitingKeys: string[] = [];

  private readonly listeners = new Set<(snapshot: InterviewVideoUploadSnapshot[]) => void>();

  private readonly releasedInterviews = new Set<number>();

  private activeCount = 0;

  private sequence = 0;

  constructor(options: QueueOptions = {}) {
    this.concurrency = Math.max(1, Math.floor(Number(options.concurrency) || 2));
    this.uploader = options.uploader || uploadInterviewVideoTask;
    this.maxRetainedUploaded = Math.max(1, Math.floor(Number(options.maxRetainedUploaded) || 50));
    this.maxAttempts = Math.max(
      1,
      Math.floor(Number(options.maxAttempts) || INTERVIEW_VIDEO_UPLOAD_MAX_ATTEMPTS)
    );
    this.retryDelaysMs = (options.retryDelaysMs || [...INTERVIEW_VIDEO_UPLOAD_RETRY_DELAYS_MS])
      .map((delayMs) => Math.max(0, Number(delayMs) || 0));
    this.retryScheduler = options.retryScheduler || ((callback, delayMs) => setTimeout(callback, delayMs));
  }

  retainInterview(interviewId: number) {
    this.releasedInterviews.delete(Number(interviewId));
  }

  releaseInterview(interviewId: number) {
    const safeInterviewId = Number(interviewId);
    this.releasedInterviews.add(safeInterviewId);
    this.tasks.forEach((task, key) => {
      if (task.interviewId !== safeInterviewId) return;
      if (task.status === 'uploaded' || task.status === 'failed') {
        this.tasks.delete(key);
      }
    });
    this.clearReleasedInterviewIfIdle(safeInterviewId);
    this.notify();
  }

  subscribe(listener: (snapshot: InterviewVideoUploadSnapshot[]) => void) {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  getSnapshot() {
    return Array.from(this.tasks.values())
      .sort((left, right) => left.sequence - right.sequence)
      .map((task): InterviewVideoUploadSnapshot => ({
        key: task.key,
        interviewId: task.interviewId,
        questionId: task.questionId,
        clientAttemptId: task.clientAttemptId,
        traceId: task.traceId,
        status: task.status,
        attempts: task.attempts,
        retryable: task.retryable,
        hasBlob: Boolean(task.blob),
        errorCode: task.errorCode,
        result: task.result,
      }));
  }

  getInterviewSnapshot(interviewId: number) {
    const safeInterviewId = Number(interviewId);
    return this.getSnapshot().filter((task) => task.interviewId === safeInterviewId);
  }

  enqueue(input: InterviewVideoUploadInput) {
    const interviewId = Number(input.interviewId);
    const questionId = Number(input.questionId);
    const clientAttemptId = String(input.clientAttemptId || '').trim();
    if (!interviewId || !questionId || !clientAttemptId || !input.blob || input.blob.size <= 0) {
      throw new Error('INVALID_VIDEO_UPLOAD_TASK');
    }

    const key = buildInterviewVideoUploadKey({ interviewId, questionId, clientAttemptId });
    if (this.tasks.has(key)) return key;

    const task: InternalVideoUploadTask = {
      ...input,
      interviewId,
      questionId,
      clientAttemptId,
      key,
      status: 'queued',
      attempts: 0,
      retryable: false,
      errorCode: null,
      result: null,
      sequence: ++this.sequence,
    };
    this.tasks.set(key, task);
    this.waitingKeys.push(key);
    this.notify();
    this.pump();
    return key;
  }

  private pump() {
    while (this.activeCount < this.concurrency && this.waitingKeys.length > 0) {
      const key = this.waitingKeys.shift();
      if (!key) continue;
      const task = this.tasks.get(key);
      if (!task || task.status !== 'queued' || !task.blob) continue;
      this.activeCount += 1;
      void this.run(task);
    }
  }

  private async run(task: InternalVideoUploadTask) {
    const uploadBlob = task.blob;
    if (!uploadBlob) {
      this.activeCount = Math.max(0, this.activeCount - 1);
      this.pump();
      return;
    }
    task.status = 'uploading';
    task.attempts += 1;
    this.notify();
    trackInterviewMetric('video_upload_background_started', {
      traceId: task.traceId,
      interviewId: task.interviewId,
      questionId: task.questionId,
      clientAttemptId: task.clientAttemptId,
      source: 'background_queue',
      requestAttempt: task.attempts,
      outcome: 'started',
    });

    try {
      const result = await this.uploader({
        interviewId: task.interviewId,
        questionId: task.questionId,
        clientAttemptId: task.clientAttemptId,
        traceId: task.traceId,
        blob: uploadBlob,
        recordedAt: task.recordedAt,
        durationSeconds: task.durationSeconds,
      });
      task.result = result;
      task.status = 'uploaded';
      task.retryable = false;
      task.errorCode = null;
      task.blob = null;
      trackInterviewMetric('video_upload_background_completed', {
        traceId: task.traceId,
        interviewId: task.interviewId,
        questionId: task.questionId,
        clientAttemptId: task.clientAttemptId,
        source: 'background_queue',
        requestAttempt: task.attempts,
        outcome: 'success',
      });
    } catch (error: any) {
      const retryable = isRetryableUploadError(error);
      task.errorCode = getUploadErrorCode(error);
      trackInterviewMetric('video_upload_background_failed', {
        traceId: task.traceId,
        interviewId: task.interviewId,
        questionId: task.questionId,
        clientAttemptId: task.clientAttemptId,
        source: 'background_queue',
        requestAttempt: task.attempts,
        outcome: 'error',
      });

      if (retryable && task.attempts < this.maxAttempts) {
        task.status = 'retry_waiting';
        task.retryable = true;
        this.scheduleRetry(task);
      } else {
        task.status = 'failed';
        task.retryable = false;
        task.blob = null;
        if (retryable) {
          trackInterviewMetric('video_upload_background_exhausted', {
            traceId: task.traceId,
            interviewId: task.interviewId,
            questionId: task.questionId,
            clientAttemptId: task.clientAttemptId,
            source: 'background_queue',
            requestAttempt: task.attempts,
            retryCount: Math.max(0, task.attempts - 1),
            outcome: 'terminal_failure',
          });
        }
      }
    } finally {
      this.activeCount = Math.max(0, this.activeCount - 1);
      this.pruneUploaded();
      this.notify();
      if (this.releasedInterviews.has(task.interviewId) && (task.status === 'uploaded' || task.status === 'failed')) {
        this.tasks.delete(task.key);
        this.clearReleasedInterviewIfIdle(task.interviewId);
        this.notify();
      }
      this.pump();
    }
  }

  private scheduleRetry(task: InternalVideoUploadTask) {
    const retryDelayIndex = Math.max(0, task.attempts - 1);
    const fallbackDelay = this.retryDelaysMs[this.retryDelaysMs.length - 1] || 0;
    const retryDelayMs = this.retryDelaysMs[retryDelayIndex] ?? fallbackDelay;
    trackInterviewMetric('video_upload_background_retry_scheduled', {
      traceId: task.traceId,
      interviewId: task.interviewId,
      questionId: task.questionId,
      clientAttemptId: task.clientAttemptId,
      source: 'background_queue',
      requestAttempt: task.attempts,
      retryCount: task.attempts,
      outcome: `retry_in_${retryDelayMs}_ms`,
    });

    try {
      this.retryScheduler(() => {
        const currentTask = this.tasks.get(task.key);
        if (currentTask !== task || currentTask.status !== 'retry_waiting' || !currentTask.blob) return;

        currentTask.status = 'queued';
        currentTask.retryable = false;
        this.waitingKeys.push(currentTask.key);
        trackInterviewMetric('video_upload_background_retry_started', {
          traceId: currentTask.traceId,
          interviewId: currentTask.interviewId,
          questionId: currentTask.questionId,
          clientAttemptId: currentTask.clientAttemptId,
          source: 'background_queue',
          requestAttempt: currentTask.attempts + 1,
          retryCount: currentTask.attempts,
          outcome: 'started',
        });
        this.notify();
        this.pump();
      }, retryDelayMs);
    } catch (_) {
      task.status = 'failed';
      task.retryable = false;
      task.blob = null;
      task.errorCode = 'VIDEO_UPLOAD_RETRY_SCHEDULING_FAILED';
      trackInterviewMetric('video_upload_background_exhausted', {
        traceId: task.traceId,
        interviewId: task.interviewId,
        questionId: task.questionId,
        clientAttemptId: task.clientAttemptId,
        source: 'background_queue',
        requestAttempt: task.attempts,
        retryCount: Math.max(0, task.attempts - 1),
        outcome: 'scheduler_failure',
      });
    }
  }

  private pruneUploaded() {
    const uploaded = Array.from(this.tasks.values())
      .filter((task) => task.status === 'uploaded')
      .sort((left, right) => left.sequence - right.sequence);
    uploaded.slice(0, Math.max(0, uploaded.length - this.maxRetainedUploaded))
      .forEach((task) => this.tasks.delete(task.key));
  }

  private clearReleasedInterviewIfIdle(interviewId: number) {
    const hasActiveTask = Array.from(this.tasks.values()).some((task) => (
      task.interviewId === interviewId && (
        task.status === 'queued'
        || task.status === 'uploading'
        || task.status === 'retry_waiting'
      )
    ));
    if (!hasActiveTask) this.releasedInterviews.delete(interviewId);
  }

  private notify() {
    if (this.listeners.size === 0) return;
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (_) {}
    });
  }
}

export const interviewVideoUploadQueue = new InterviewVideoUploadQueue({ concurrency: 2 });
